import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { authFetch } from "../api/api";
import Card from "../UI/Card";
import { useTranslation } from "react-i18next";


const BASE_URL = process.env.REACT_APP_DJANGO_BACKEND;

const SupplierDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [supplier, setSupplier] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [cities, setCities] = useState([]);

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");

  const { t } = useTranslation("SuppliersPage");

  // ===== LOAD SUPPLIER =====
  const loadSupplier = async () => {
    const res = await authFetch(
      `${BASE_URL}/api/suppliers/suppliers/${id}/`
    );
    const data = await res.json();
    setSupplier(data);
  };

  // ===== LOAD CITIES =====
  const loadCities = async () => {
    const res = await authFetch(`${BASE_URL}/api/cities/`);
    const data = await res.json();
    setCities(data);
  };

  // ===== LOAD ADDRESSES =====
  const loadAddresses = async () => {
    const res = await authFetch(
      `${BASE_URL}/api/suppliers/adresses/`
    );
    const data = await res.json();

    const filtered = data.filter(
      (a) => String(a.supplier) === String(id)
    );

    setAddresses(filtered);
  };

  // ===== LOAD PRODUCTS =====
  const loadProducts = async () => {
    const res = await authFetch(`${BASE_URL}/api/products/`);
    const data = await res.json();

    const filtered = data.filter(
      (p) => String(p.supplier?.id) === String(id)
    );

    setProducts(filtered);
  };

  useEffect(() => {
    loadSupplier();
    loadAddresses();
    loadCities();
    loadProducts();
  }, [id]);

  // ===== FILTER PRODUCTS =====
  const filteredProducts = useMemo(() => {
    if (!search) return products;

    return products.filter((p) =>
      (p.name_en || "").toLowerCase().includes(search.toLowerCase()) ||
      (p.name_cn || "").toLowerCase().includes(search.toLowerCase())
    );
  }, [search, products]);

  // ===== CITY NAME =====
  const getCityName = (cityId) => {
    const found = cities.find((c) => String(c.id) === String(cityId));
    return found ? found.name : cityId;
  };

  if (!supplier) return <div className="p-6">Загрузка...</div>;

  return (
    <div className="w-full max-w-4xl mx-auto mt-10 space-y-6">

      {/* ===== HEADER ===== */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{supplier.name}</h1>
      </div>

      {/* ===== INFO CARD ===== */}
      <Card className="space-y-2">
        <p>{t("warehouses")}: {supplier.warehouses || "-"}</p>
        <p>{t("payment_terms")}: {supplier.delivery_terms || "-"}</p>
        <p>{t("certificates")}: {supplier.certificates || "-"}</p>
        <p>{t("comment")}: {supplier.comment || "-"}</p>
      </Card>

      {/* ===== BALANCE ===== */}
      <Card className="space-y-2 bg-gray-50">
        <h3 className="font-bold"> Баланс</h3>

        <p>
          Юани (CNY): <span className="font-semibold">0 ¥</span>
        </p>

        <p className="text-xs text-gray-500">
          (заглушка — позже подключим API)
        </p>
      </Card>


      {/* ===== ОФИЦИАЛЬНАЯ ИНФОРМАЦИЯ ===== */}
      <Card className="space-y-2 bg-gray-50">
        <h3 className="font-bold"> Официальная информация</h3>

        <p>
          Телефон: {supplier.phone}
        </p>

        <p>
          E-mail: {supplier.email}
        </p>


        <p>
          Официальное название: {supplier.officail_name}
        </p>


        <p>
          Юридический адрес: {supplier.official_adress}
        </p>
        <p>
          НДС: {supplier.value_added_tax}
        </p>

        <p>
          税号: {supplier.tax_number}
        </p>

      {/* BANK ACCOUNTS */}
      {supplier.bank_accounts && supplier.bank_accounts.length > 0 ? (
        <div className="mt-4">
          <h3 className="font-bold">
            Банковские данные
          </h3>

          {supplier.bank_accounts.map((bank) => (
            <Card key={bank.id} className="mt-2 p-3">
              <p>
                Банк: {bank.bank_name || "-"}
              </p>

              <p>
                Счёт: {bank.account_number || "-"}
              </p>

              <p>
                SWIFT: {bank.swift || "-"}
              </p>

              <p>
                SNAPS: {bank.bank_code || "-"}
              </p>
              <p>
                Адрес банка: {bank.bank_adress || "-"}
              </p>
            </Card>
          ))}
        </div>
      ) : (
        <p className="text-gray-500">
          Банковские данные отсутствуют
        </p>
      )}

  
      </Card>


      {/* ===== PRODUCTS ===== */}
      <div className="space-y-3">
        <h2 className="text-xl font-bold"> {t("supplier_products")}</h2>

        <input
          className="w-full border p-2 rounded-xl"
          placeholder={t("search_product")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <div className="space-y-3">
          {filteredProducts.length === 0 ? (
            <p className="text-gray-500">{t("no_products")}</p>
          ) : (
            filteredProducts.map((p) => (
              <Card
                key={p.id}
                hoverable
                className="p-4 cursor-pointer"
                onClick={() => navigate(`/products/${p.id}`)}
              >
                <div className="flex justify-between items-center">

                  {/* LEFT */}
                  <div className="flex items-center gap-4">

                    {p.image ? (
                      <img
                        src={
                          p.image.startsWith("http")
                            ? p.image
                            : `${BASE_URL}${p.image}`
                        }
                        className="w-16 h-16 object-cover rounded"
                        alt={p.name_en}
                      />
                    ) : (
                      <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center text-xs">
                        no img
                      </div>
                    )}

                    <div>
                      <p className="font-semibold">
                        {p.name_en} / {p.name_cn}
                      </p>

                      <p className="text-sm text-gray-500">
                         {p.purchase_price || "-"} ¥
                      </p>
                    </div>
                  </div>

                </div>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* ===== ADDRESSES ===== */}
      <div>
        <h3 className="font-bold"> {t("addresses")}</h3>

        {addresses.length > 0 ? (
          addresses.map((addr) => (
            <Card key={addr.id} className="mt-2">
              <div> {addr.full_address}</div>
              <div> {getCityName(addr.city)}</div>
            </Card>
          ))
        ) : (
          <p className="text-gray-500">{t("no_addresses")}</p>
        )}
      </div>

    </div>
  );
};

export default SupplierDetail;